import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactTable from "react-table";
import { createColumns, DEFAULT_SORT, taskListFilterOutputMappings } from "./lib/constants";
import ReactTablePagination from "./components/pagination";
import { useWindowVisible } from "../table/lib/hooks";
import { usePagination } from "shared/hooks/use-pagination";
import { useSearchTasks } from "shared/hooks/api/use-search-task";
import { getStorageValue, setStorageValue } from "shared/storage";
import { usePrevious } from "shared/hooks/use-previous";
import { useSettings } from "../../shared/hooks/use-settings";

export const TaskList = ({
    onDisableAdvancedFilters,
    forceRefresh,
    selectedProducts,
    appendSettings,
    visibleColumns,
    onBINClick,
    onClick,
    locations,
    types,
    assignmentOptions,
    userAssignmentOptions,
    onUpdateTeamAssignments,
    onUpdateUserAssignments,
    sourcePage,
    onTaskRowClick,
    onSelectProduct,
    storageSortValue,
    storageFilterValue,
    storageSettingsValue = "task-list-settings",
    task,
    externalFilters,
    pollTimeout = 0,
}) => {
    const { settings } = useSettings();
    const prevExternalFilters = usePrevious(externalFilters);
    const storedSettings = getStorageValue("task-list-settings") || {};
    const storedSort = getStorageValue(storageSortValue);
    const storedFilters = getStorageValue(storageFilterValue);
    const storedAdvancedFilters = getStorageValue(`${task}-advanced-filters`);
    const isWindowVisible = useWindowVisible();
    const pollingTime = useRef(0);
    const [
        { pageNumber, pageSize, sortBy, totalCount, totalPages, searchParams, pagingParams, filteredValues },
        { setPageSize, setPage, setSortBy, setTotalCount, search },
    ] = usePagination({
        defaultFilters: storedFilters,
        defaultValues: {
            pageSize: storedSettings.pageSize || 10,
            sortBy: storedSort || DEFAULT_SORT,
        },
        filterOutputMappings: {
            assignedUserInfo: taskListFilterOutputMappings.assignedUserInfo,
        },
        onSearchParamsSet: (filters) => {
            // store only basic filters on any change - advanced filters are set in a different place
            if (!externalFilters) {
                setStorageValue(storageFilterValue, filters);
            }
        },
    });
    const {
        mutate: searchTasks,
        data: taskList,
        isSuccess,
    } = useSearchTasks(task, { [taskListFilterOutputMappings.assignedUserInfo]: "assignedUserInfo" });
    const [taskListItems, setTaskListItems] = useState([]);
    const selectedProductsIds = useMemo(() => selectedProducts.map(({ context }) => context.key), [selectedProducts]);

    useEffect(() => {
        if (forceRefresh) {
            searchTasks({ searchParams, pagingParams, isUsingExternalFilters: !!externalFilters });
        }
    }, [forceRefresh, externalFilters, pagingParams]);

    useEffect(() => {
        // apply/update advanced filters
        if (externalFilters) {
            search(externalFilters);

            return;
        }

        // if advanced filters were cleared, re-run search on normal filters with stored values
        if (prevExternalFilters && !externalFilters) {
            search(storedFilters);
        }
    }, [prevExternalFilters, externalFilters]);

    useEffect(() => {
        if (storedFilters && !storedAdvancedFilters) {
            search(storedFilters);
        }
    }, []);

    useEffect(() => {
        const runSearch = () => {
            if (!isWindowVisible) {
                return;
            }

            if (searchParams.length > 0) {
                searchTasks({ searchParams, pagingParams, isUsingExternalFilters: !!externalFilters });
            }

            if (pollTimeout && isWindowVisible) {
                pollingTime.current = setTimeout(() => {
                    runSearch();
                }, pollTimeout);
            }
        };

        clearTimeout(pollingTime.current);
        runSearch();

        return () => {
            clearTimeout(pollingTime.current);
        };
    }, [searchParams, pagingParams, isWindowVisible]);

    useEffect(() => {
        setStorageValue(storageSettingsValue, { pageSize: pageSize });
    }, [pageSize, storageSettingsValue]);

    useEffect(() => {
        setStorageValue(storageSortValue, sortBy);
    }, [sortBy]);

    useEffect(() => {
        if (isSuccess) {
            onDisableAdvancedFilters(false);
            setTotalCount(taskList.totalRecordCount);
            setTaskListItems(taskList.items);
        }
    }, [isSuccess, taskList]);

    const updateSelectedProduct = useCallback(
        (selectedRow, isRemove) => {
            if (isRemove === "true") {
                onSelectProduct(selectedProducts.filter(({ key }) => key !== selectedRow.key));
            } else {
                onSelectProduct([...selectedProducts, selectedRow]);
            }
        },
        [onSelectProduct, selectedProducts]
    );

    const selectedAllProduct = useCallback((selections) => onSelectProduct([...selections]), [onSelectProduct]);

    const handlePageChange = (page) => {
        onSelectProduct([]);
        setPage(page);
    };

    const columns = useMemo(() => {
        return createColumns(
            selectedProductsIds,
            selectedAllProduct,
            updateSelectedProduct,
            visibleColumns,
            onBINClick,
            onClick,
            locations,
            types,
            assignmentOptions,
            userAssignmentOptions,
            onUpdateTeamAssignments,
            onUpdateUserAssignments,
            sourcePage,
            !externalFilters,
            settings
        );
    }, [
        selectedProductsIds,
        selectedAllProduct,
        updateSelectedProduct,
        visibleColumns,
        onBINClick,
        onClick,
        locations,
        types,
        assignmentOptions,
        userAssignmentOptions,
        onUpdateTeamAssignments,
        onUpdateUserAssignments,
        sourcePage,
        externalFilters,
        settings,
    ]);

    return (
        <ReactTable
            manual
            appendSettings={appendSettings}
            data={taskListItems}
            filtered={filteredValues}
            sortable
            columns={columns}
            multiSort
            minRows={5}
            totalRecords={totalCount}
            showPagination
            PaginationComponent={ReactTablePagination}
            onPageChange={handlePageChange}
            onPageSizeChange={setPageSize}
            onSortedChange={setSortBy}
            onFilteredChange={search}
            sorted={sortBy}
            page={pageNumber}
            pageSize={pageSize}
            pages={totalPages}
            getTrProps={onTaskRowClick}
        />
    );
};
